<template>
  <div class="wrap state-scanner">
    <div class="scanner" v-if="!externalScanner" v-on:click="nextCamera">
      <video style="object-fit: cover; object-position: 50% 50%" id="preview" playsinline></video>
    </div>
    <div class="content">
      <div class="container">
        <div class="row">
          <div class="col-xs-12">
            <div v-if="debug">
              <button class="btn btn-info" v-on:click="manualScan">Manual scan</button>
              <input type="text" v-model="manualTicket" />
            </div>
            <ul class="list-group">
              <li class="list-group-item selected-event" v-for="se in selectedEvents"><span class="item-text"><span class="item-name">{{ se.text }}</span><span class="item-times"><span class="item-date">{{ formatDate(se.start_date) }}</span><span class="item-resource">{{ se.resource_name }}<span v-if="showCapacity[se.id]"> ({{ showCapacity[se.id].capacity }})</span></span></span><span class="item-capacity" v-if="showCapacity[se.id]"><span class="capacity-scanned">{{ showCapacity[se.id].confirmed }}</span><span class="capacity-reserved">{{ showCapacity[se.id].reservations }}</span></span></span></li>
            </ul>  
            <ul class="list-group">
              <li class="list-group-item selected-resource" v-for="sr in selectedResources"><span class="item-text"><span class="item-name">{{ sr.name }}</span></span></li>
            </ul>  
            <pre v-if="debug">
            {{ result }}
            </pre>
            
            <div v-if="!ticket">
              <div v-if="scanStatus == 'invalid'" class="alert alert-danger">
                The code is invalid.
              </div>
              <div v-if="scanStatus == 'shop-mismatch'" class="alert alert-danger">
                This code is for another Johku merchant.
              </div>
              <div v-if="scanStatus == 'confirm-failed'" class="alert alert-danger">
                Could not confirm the ticket. Please scan it again.
              </div>
              <div v-if="scanStatus == 'reading'" class="alert alert-info">
                Reading code...
              </div>
              <div v-if="scanStatus == 'loading'" class="alert alert-info">
                Loading ticket...
              </div>
              <div v-if="!scanStatus" class="alert alert-info">
                Please scan another code.
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="scan-overlay alert alert-success" v-if="ticket && isValidForActiveTarget && ticket.ownSeat_ticket.length == 1 && parseInt(ticket.ownSeat_ticket[0].status, 10) === 0">
        <div class="scan-text-wrap">
          <div class="scan-text">
            <div class="reservation-info"><h4 class="reservation-show">{{ ticket.reservation.product_name }}</h4><p class="reservation-details"><span class="reservation-time">{{ formatDateFromTimestamp(ticket.reservation.start_ts) }}</span> <span class="reservation-resource">{{ ticketResourceName }}</span></p></div>
            <h2 class="scan-ok center-text text-center">
              <span v-for="seat in ticket.ownSeat_ticket">
                {{ getSeatDescription(seat.seat) }}
              </span>
            </h2>
            <div class="ticket-items" v-if="ticket.ticketitems">
              <div class="ticket-item" v-for="item in ticket.ticketitems">
                {{ item.count }} x {{item.product_name}}
              </div>
            </div>
          </div>
        </div>
        <div class="scan-buttons">
          <button type="button" class="btn btn-success btn-sm btn-fullwidth btn-cancel-seats" v-on:click="cancel">Close</button>
        </div>
      </div>
      <div class="scan-overlay alert alert-warning" v-if="ticket && isValidForActiveTarget && (ticket.personal || ticket.ownSeat_ticket.length > 1 || (ticket.ownSeat_ticket.length == 1 && parseInt(ticket.ownSeat_ticket[0].status, 10) !== 0))">
        <div class="scan-text-wrap">
          <div class="scan-text">
            <div class="reservation-info"><h4 class="reservation-show">{{ ticket.reservation.product_name }}</h4><p class="reservation-details"><span class="reservation-time">{{ formatDateFromTimestamp(ticket.reservation.start_ts) }}</span> <span class="reservation-resource">{{ ticketResourceName }}</span></p></div>
            <div class="reservation-info well" v-if="ticket.personal">This ticket is only usable by {{ ticket.personal }}</div>
            <div class="ticket-items" v-if="ticket.ticketitems">
              <div class="ticket-item" v-for="item in ticket.ticketitems">
                {{ item.count }} x {{item.product_name}}
              </div>
            </div>
            <div class="confirm-buttons">
              <button type="button" :class="'btn btn-' + seatClass(seat)" v-on:click="toggleSeat(seat)" v-for="seat in ticket.ownSeat_ticket">
                {{ getSeatDescription(seat.seat) }}
              </button>
            </div>
          </div>
        </div>
        <div class="scan-buttons">
          <button type="button" class="btn btn-success btn-confirm-seats btn-lg btn-fullwidth" :disabled="!(this.selectedSeats.length > 0)" v-on:click="confirmSeats">{{ selectedSeats.length ? 'Confirm selected (' + selectedSeats.length + ')' : 'Select seats' }}</button>
          <button type="button" class="btn btn-default btn-sm btn-fullwidth btn-cancel-seats" v-on:click="cancel">Cancel</button>
        </div>
      </div>
      <div class="scan-overlay alert alert-danger" v-if="ticket && !isValidForActiveTarget">
        <div class="scan-text-wrap">
          <div class="scan-text">
            <span class="reservation-info">
              <p><span v-if="this.selectedEvents && this.selectedEvents.length">The ticket is not valid for the current show.</span>
              {{ ticketDetails }}</p>
              <span v-if="ticket && ticket.reservation">
                <p>The scanned ticket is for 
                  <strong>{{ ticket.reservation.product_name }} / {{ formatDateFromTimestamp(ticket.reservation.start_ts) }} ({{ ticketResourceName }})</strong>.
                </p>
              </span>
            </span>
          </div>
          <div class="overlay-button" v-on:click="cancel"></div>
        </div>
        <div class="scan-buttons">
          <button type="button" class="btn btn-default btn-sm btn-fullwidth btn-cancel-seats" v-on:click="cancel">Cancel</button>
        </div>
      </div>
    </div>
    <div class="container transparent footer-container">
      <button class="btn btn-info scan-settings footer-button-halfwidth left-button" v-on:click="settings">Settings</button>
      <button class="btn btn-danger scan-logout footer-button-halfwidth right-button" v-on:click="logout">Logout</button>
      <div class="clearfix"></div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Instascan from 'instascan'
import moment from 'moment'
import { throttle } from 'lodash'
import debounce from 'lodash/debounce'
import io from 'socket.io-client/dist/socket.io.js'
import { authFetch } from '../lib/api'

export default {
  name: 'Scan',
  data () {
    return {
      activeCameraId: null,
      activeCameraIndex: 1,
      cameras: [],
      ticket: null,
      ticketDetails: '',
      scanStatus: false,
      selectedSeats: [],
      showCapacity: {},
      buffer: [],
      lastKey: false,
      result: '\n',
      manualTicket: '',
      lastAutomated: false,
      debug: false,
      listener: false,
    }
  },
  computed: {
    ...mapGetters([
      'apiKey',
      'baseDomain',
      'bridgeUri',
      'selectedEvents',
      'selectedResources',
      'selectedTimes',
      'externalScanner',
      'defaultShopId'
    ]),
    shopId () {
      return this.defaultShopId
    },
    isValidForActiveTarget () {
      if (!this.ticket || !this.ticket.ownSeat_ticket || !this.ticket.reservation) {
        return false
      }
      if (parseInt(this.ticket.reservation.status, 10) < 1) {
        this.ticketDetails = 'The reservation has not been confirmed.'
        return false
      }
      if (this.selectedEvents && this.selectedEvents.length) {
        return this.isValidForActiveShow
      } else {
        return this.isValidForActiveResource
      }
    },
    isValidForActiveShow () {
      if (!this.showIdMatches()) {
        this.ticketDetails = 'The selected show id and the ticket\'s show id do not match.'
        return false
      }
      /*
      var eventDate = moment(this.selectedEvent.start_date)
      var ticketDate = moment.unix(parseInt(this.ticket.reservation.start_ts, 10))
      if (!eventDate.isSame(ticketDate)) {
        this.ticketDetails = 'The event date and the ticket date do not match.'
        this.result += eventDate.format('D.M.YYYY H.mm') + '\n'
        this.result += ticketDate.format('D.M.YYYY H.mm') + '\n'
        return false
      }
      */
      if (!this.resourceIdMatchesShow()) {
        this.ticketDetails = 'The seats in this ticket are for a different resource.'
        return false
      }
      return true

    },
    isValidForActiveResource () {
      if (!this.resourceIdMatches()) {
        this.ticketDetails = 'The seats in this ticket are for a different resource.'
        return false
      }
      if (!this.ticketTimeMatches()) {
        this.ticketDetails = 'The ticket is not valid for the selected time.'
        return false
      }
      return true
    },
    ticketResourceName () {
      if (!this.ticket || !this.ticket.ownSeat_ticket || !this.ticket.reservation) {
        return ''
      }
      if(!this.selectedEvents || !this.selectedEvents.length) {
        return this.ticketResourceNameWithoutEvents
      }
      for (var i = 0; i < this.ticket.ownSeat_ticket.length; i++) {
        for (var j = 0; j < this.selectedEvents.length; j++) {
          let se = this.selectedEvents[j]
          if (se.resource_id == this.ticket.ownSeat_ticket[i].seat.resource_id) {
            return se.resource_name
          }
        }
      }
      return ''
    },
    ticketResourceNameWithoutEvents () {
      if(!this.selectedResources || !this.selectedResources.length) {
        return ''
      }
      for (var i = 0; i < this.ticket.ownSeat_ticket.length; i++) {
        for (var j = 0; j < this.selectedResources.length; j++) {
          let sr = this.selectedResources[j]
          if (sr.id == this.ticket.ownSeat_ticket[i].seat.resource_id) {
            return sr.name
          }
        }
      }
      return ''
    }
  },
  methods: {
    getSeatDescription (seat) {
      if (seat.header || seat.title) {
        return (seat.header ? seat.header + ' / ' : '') + seat.title
      }
      return seat.name
    },
    showIdMatches () {
      if (!this.selectedEvents || !this.selectedEvents.length) {
        return false
      }
      for (var i = 0; i < this.selectedEvents.length; i++) {
        let se = this.selectedEvents[i]
        if (se.id == this.ticket.show_id) {
          return true
        }
      }
      return false
    },
    resourceIdMatchesShow () {
      if (!this.selectedEvents || !this.selectedEvents.length) {
        return false
      }
      for (var i = 0; i < this.ticket.ownSeat_ticket.length; i++) {
        for (var j = 0; j < this.selectedEvents.length; j++) {
          let se = this.selectedEvents[j]
          if (se.resource_id == this.ticket.ownSeat_ticket[i].seat.resource_id) {
            return true
          }
        }
      }
      return false
    },
    resourceIdMatches () {
      if (!this.selectedResources || !this.selectedResources.length) {
        return false
      }
      for (var i = 0; i < this.ticket.ownSeat_ticket.length; i++) {
        for (var j = 0; j < this.selectedResources.length; j++) {
          let sr = this.selectedResources[j]
          if (sr.id == this.ticket.ownSeat_ticket[i].seat.resource_id) {
            return true
          }
        }
      }
      return false
    },
    ticketTimeMatches () {
      if (!this.selectedTimes || !this.selectedTimes.length) {
        return false
      }
      for (var i = 0; i < this.selectedTimes.length; i++) {
        let ts = this.selectedTimes[i]
        if (this.ticket.reservation.start_ts == ts) {
          return true
        }
      }
      return false
    },
    formatDate (date) {
      return moment(date).format('D.M.YYYY H.mm')
    },
    formatDateFromTimestamp (ts) {
      return moment.unix(parseInt(ts, 10)).format('D.M.YYYY H.mm')
    },
    cancel: function () {
      this.ticket = null
      this.selectedSeats = []
      this.scanStatus = false
      this.ticketDetails = ''
    },
    settings: function () {
      this.$router.push('/')
    },
    logout: function () {
      this.$store.dispatch('logout')
      this.$router.push('/login')
    },
    seatClass: function (seat) {
      if (seat.status == 1) {
        return 'danger'
      }
      if (this.selectedSeats.indexOf(seat.seat_id) !== -1) {
        return 'primary'
      }
      return 'default'
    },
    manualScan: function () {
      this.processScan(this.manualTicket)
    },
    formatName: function (name) {
      return name || '(unknown)'
    },
    selectCamera: function (camera) {
      this.activeCameraId = camera.id
      this.scanner.start(camera)
    },
    nextCamera: function () {
      let nextCameraIndex = this.activeCameraIndex + 1
      if(nextCameraIndex === this.cameras.length) {
        nextCameraIndex = 0
      }
      this.activeCameraIndex = nextCameraIndex
      this.selectCamera(this.cameras[this.activeCameraIndex])
      return true
    },
    processScan: function (content, image) {
      let $this = this
      this.selectedSeats = []
      this.scanStatus = false
      this.ticketDetails = ''
      var found = false
      this.result += 'processScan\n'
      this.result += content + '\n'
      let shopId = false
      let identifier = false
      if(found = content.match(/https:\/\/?[a-z\-\.]+\/([^\/]+)\/qr\/([^\/]+)/)) {
        shopId = found[1]
        identifier = found[2]
        this.result += shopId + '\n'
        this.result += identifier + '\n'
      } else if (content.match(/.+\/.+/)) {
        let tArr = content.split('/')
        shopId = tArr[0]
        identifier = tArr[1]
      } else {
        $this.ticket = null
        this.scanStatus = 'invalid'
        this.result += 'invalid\n'
        setTimeout(() => { this.scanStatus = false }, 3000)
        return
      }
      if (shopId !== this.shopId) {
        $this.ticket = null
        this.scanStatus = 'shop-mismatch'
        this.result += 'mismatch\n'
        setTimeout(() => { this.scanStatus = false }, 3000)
        return
      }
      this.result += 'fetch\n'
      this.scanStatus = 'loading'
      fetch('https://' + this.baseDomain + '/' + shopId + '/tickets/' + identifier + '.json')
      .then((response) => {
        this.result += 'response\n'
        response.json().then(data => {
          this.result += data + '\n'
          this.scanStatus = false
          $this.ticket = data
          $this.selectSeats(identifier)
          if($this.selectedSeats.length === 1 && $this.ticket.ownSeat_ticket.length === 1 && $this.isValidForActiveTarget && !$this.ticket.personal) {
            // Only one seat that was selected, perform automatic approval - unless this was paid with ePassi or Smartum, which means we need to check id
            $this.confirmSeats(true)
          }
        })
      })
    },
    selectSeats: function (identifier) {
      identifier = identifier.substring(1)
      if (identifier === this.ticket.identifier) {
        for (var i = 0; i < this.ticket.ownSeat_ticket.length; i++) {
          if (parseInt(this.ticket.ownSeat_ticket[i].status, 10) === 0) {
            this.selectedSeats.push(this.ticket.ownSeat_ticket[i].seat_id)
          }
        }
      } else {
        for (var i = 0; i < this.ticket.ownSeat_ticket.length; i++) {
          if (this.ticket.ownSeat_ticket[i].identifier == identifier && parseInt(this.ticket.ownSeat_ticket[i].status, 10) === 0) {
            this.selectedSeats.push(this.ticket.ownSeat_ticket[i].seat_id)
          }
        }
      }
    },
    toggleSeat: function (seat) {
      if (parseInt(seat.status, 10) === 0) {
        if (this.selectedSeats.indexOf(seat.seat_id) !== -1) {
          this.selectedSeats.splice(this.selectedSeats.indexOf(seat.seat_id), 1)
        } else {
          this.selectedSeats.push(seat.seat_id)
        }
      }
    },
    confirmSeats: function (automatedApproval) {
      if(typeof automatedApproval !== 'boolean') {
        automatedApproval = false
      }
      this.lastAutomated = automatedApproval
      if (!this.selectedSeats.length) {
        return
      }
      let $this = this
      let ids = []
      for (var i = 0; i < this.ticket.ownSeat_ticket.length; i++) {
        if (this.selectedSeats.indexOf(this.ticket.ownSeat_ticket[i].seat_id) !== -1) {
          ids.push(this.ticket.ownSeat_ticket[i].id)
        }
      }
      let requests = []
      if (ids.length) {
        for (i = 0; i < ids.length; i++) {
          requests.push(
            authFetch($this.baseDomain, $this.shopId, 'rest/patch/tickets/' + ids[i] + '.json?id=' + ids[i] + '&status=1', $this.apiKey).then(response => {
              if (!response.ok) {
                throw new Error('Failed to confirm seat ' + ids[i] + ' (HTTP ' + response.status + ')')
              }
            })
          )
        }
      }
      Promise.all(requests).then(() => {
        if(!$this.lastAutomated) {
          $this.ticket = null
          $this.selectedSeats = []
        }
      }).catch(() => {
        $this.ticket = null
        $this.selectedSeats = []
        $this.scanStatus = 'confirm-failed'
        setTimeout(() => { $this.scanStatus = false }, 4000)
      })
    },
    flushBuffer: function () {
      let code = this.buffer.join('')
      this.buffer = []
      if (code) {
        this.processScan(code)
      }
    },
    throttledUpdate: throttle(function () { this.updateSeatAllocations() }, 500, { leading: false, trailing: true }),
    updateSeatAllocations: function () {
      let $this = this
      let idStr = ''
      let ids = []
      for (var i = 0; i < this.selectedEvents.length; i++) {
        let se = this.selectedEvents[i]
        ids.push(se.id)
      }
      idStr = ids.join(',')
      if (idStr) {
        authFetch(this.baseDomain, this.shopId, 'showschedule.json?ids=' + idStr, this.apiKey)
        .then((response) => {
          response.json().then(data => {
            if (data && data.data) {
              $this.showCapacity = {}
              for (let j = 0; j < data.data.length; j++) {
                let s = data.data[j]
                $this.showCapacity[s.id] = {
                  capacity: s.resource_capacity,
                  reservations: s.resource_reservations,
                  confirmed: s.resource_confirmed
                }
              }
            }
          })
        })
      }
    }
  },
  mounted () {
    let $this = this
    this.updateSeatAllocations()
    if (!this.externalScanner) {
      $this.scanner = new Instascan.Scanner({ 
        video: document.getElementById('preview'),
        scanPeriod: 5,
        mirror: false
      })
      $this.scanner.addListener('scan', function (content, image) {
        $this.processScan(content, image)
      })
      Instascan.Camera.getCameras().then(function (cameras) {
        $this.cameras = cameras
        if (cameras.length > 1) {
          $this.activeCameraId = cameras[1].id
          $this.scanner.start(cameras[1])
          $this.activeCameraIndex = 1
        } else {
          if(cameras.length === 1) {
            $this.activeCameraId = cameras[0].id
            $this.scanner.start(cameras[0])
            $this.activeCameraIndex = 0
          } else {
            console.error('No cameras found.')
          }
        }
      }).catch(function (e) {
        console.error(e)
      });
    } else {
      let flushTimeout = false
      $this.listener = (event) => {
        clearTimeout(flushTimeout)
        if (event && event.key && event.key.match(/^[0-9a-z\:\/\-\_\.]$/i)) {
          this.scanStatus = 'reading'
          this.buffer.push(event.key)
          flushTimeout = setTimeout(() => { this.flushBuffer() }, 500)
        } 
        if (event && (event.keyCode === 13 || event.key === 'Enter')) {
          clearTimeout(flushTimeout)
          if (this.buffer.length) {
            this.flushBuffer()
          }
        } 
      }
      window.addEventListener('keypress', $this.listener)
    }
    this.socket = io(this.bridgeUri, {
      query: {
        shopId: this.shopId,
        apiKey: this.apiKey
      },
      transports: ['websocket'],
      reconnection: true
    })
    this.socket.on('connect', function () {
      console.info('Socket.IO connected')
      $this.socket.emit('channel', { action: 'join', shopId: $this.shopId })
    })
    this.socket.on('disconnect', function () {
      console.warn('Socket.IO connection closed')
    })
    this.socket.on('message', function (msg) {
      if (msg.type === 'availabilityUpdate') {
        for (let i = 0; i < $this.selectedEvents.length; i++) {
          let se = $this.selectedEvents[i]
          let productMatch = msg.data && msg.data.affectedProducts && msg.data.affectedProducts.findIndex(item => String(item) === String(se.product_id)) > -1
          let resourceMatch = msg.data && msg.data.affectedResources && msg.data.affectedResources.findIndex(item => String(item) === String(se.resource_id)) > -1
          if (productMatch || resourceMatch) {
            $this.throttledUpdate()
            break
          }
        }
      }
    })
  },
  beforeDestroy: function () {
    if (this.listener) {
      window.removeEventListener('keypress', this.listener)
      this.listener = false
    }
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }
  }
}
</script>


// WEBPACK FOOTER //
// src/components/Scan.vue